/* Podgląd 3D kuchni z planu IKEA (sekcja planer-zestaw). Scena z usługi planera (scena_3d.py): prostopadłościany
   w mm w układzie planera (x, y na podłodze, z w górę) + wielokąty blatów i podłogi. three.js z jsDelivr, pobierany
   już w czasie czekania na wynik (zaladuj()). Ściana, która rozdziela kamerę i środek kuchni, chowa się razem ze swoimi
   oknami i drzwiami. Rysuje tylko na żądanie (ruch, wygląd, zmiana rozmiaru), bez stałej pętli klatek.
   Wygląd frontów jak w konfiguratorze: lakier = rysunek frontu z KnfFront (knf-front.js) zrasteryzowany na teksturę lica,
   osobną na unikalny front (Gładki, cokoły i witryny to sam kolor - bez tekstury); drewno = skan arkusza w skali mm
   (jeden na styl), UV lica liczone jak wzór w KnfFront.
   Najpierw kolor płaski, tekstury wchodzą, gdy gotowe; przy błędzie (np. skażony canvas w Safari) zostaje kolor płaski.
   API: PlanerZestaw3D.pokaz(el, scena, { kolor, styl, kat, tex, opis }) -> Promise<{ ustaw({ kolor, styl, kat }) -> Promise,
   kolor(hex), zniszcz() }>; tex = skany drewna z data-tex sekcji (jak w konfiguratorze). */
(() => {
  const CDN = "https://cdn.jsdelivr.net/npm/three@0.160.0";
  let lib = null, gl = null;
  function webgl() {             // bez WebGL nie pobieramy biblioteki
    if (gl === null) {
      try {
        const x = document.createElement("canvas"), c = x.getContext("webgl2") || x.getContext("webgl");
        gl = !!c;
        if (c && c.getExtension("WEBGL_lose_context")) c.getExtension("WEBGL_lose_context").loseContext();
      } catch (e) { gl = false; }
    }
    return gl;
  }
  // nieudany import nie zostaje zapamiętany: kolejny wynik próbuje pobrać bibliotekę od nowa
  const zaladuj = () => !webgl() ? Promise.reject(new Error("brak WebGL")) : lib || (lib = Promise.all([
    import(CDN + "/+esm"), import(CDN + "/examples/jsm/controls/OrbitControls.js/+esm")
  ]).catch(e => { lib = null; throw e; }));
  // gęstość tekstur lica: 1 px/mm, na telefonie i przy małej pamięci 0,6 px/mm; najwyżej 1024 px na bok
  const PXMM = window.innerWidth <= 600 || navigator.deviceMemory <= 4 ? 0.6 : 1, MAXPX = 1024;
  const dataUri = {};            // miniatura skanu jako data URI (SVG rysowany jako obraz nie pobiera plików)
  const naDataUri = u => dataUri[u] || (dataUri[u] = fetch(u).then(r => r.blob()).then(b => new Promise((ok, zle) => {
    const f = new FileReader(); f.onload = () => ok(f.result); f.onerror = zle; f.readAsDataURL(b);
  })).catch(e => { delete dataUri[u]; throw e; }));

  async function pokaz(el, scena, o = {}) {
    const [THREE, { OrbitControls }] = await zaladuj();
    const M = v => v / 1000;
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    const cv = renderer.domElement;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    cv.setAttribute("role", "img");
    if (o.opis) cv.setAttribute("aria-label", o.opis);
    el.replaceChildren(cv);
    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#f4f2ee");

    const mat = (color, op) => new THREE.MeshStandardMaterial({ color, roughness: 0.8, ...op });
    let wyg = { hex: o.kolor || "#f7f7f2", styl: o.styl || "Gładki", kat: o.kat || "lakier" };
    const DREWNO = "#c9b08a";       // drewno, zanim wczyta się skan (jak w KnfFront)
    const front = mat(wyg.kat === "lakier" ? wyg.hex : DREWNO, { roughness: 0.55 });   // kolor płaski i boki frontów lakierowanych
    // krawędzie frontów: przy jasnych kolorach ciemniejsze, żeby biel nie zlewała się z korpusami i ścianą
    const ciemn = c => { c = new THREE.Color(c); return c.multiplyScalar(0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b > 0.8 ? 0.6 : 0.78); };
    const kraw = new THREE.LineBasicMaterial({ color: ciemn(front.color) });
    const szklo = mat("#a9c2cc", { transparent: true, opacity: 0.45, roughness: 0.05 });
    const MAT = {
      front, cokol: front, witryna: front, korpus: mat("#e3e0da"), sciana: mat("#e2ddd3", { roughness: 1 }),
      okno: mat("#bcd6e4", { transparent: true, opacity: 0.55, roughness: 0.1 }), drzwi_pok: mat("#e6e0d5"),
      agd: mat("#a4a6a8", { metalness: 0.3, roughness: 0.45 }), blat: mat("#cbc5bb", { roughness: 0.7 }), piekarnik: mat("#202124", { metalness: 0.2, roughness: 0.25 }),
      plyta: mat("#141414", { roughness: 0.2 }), zlew: mat("#c4c7ca", { metalness: 0.7, roughness: 0.3 }),
      inne: mat("#dedbd5", { transparent: true, opacity: 0.28, depthWrite: false })   // bryły spoza IKEA nie zasłaniają frontów
    };

    const sciany = [], korpusy = [], fronty = [], kuchnia = new THREE.Box3();
    const zTypem = scena.bryly.some(b => b.k === "front" && b.t);   // starsza usługa nie podaje typu elementu
    let nx = 0, nz = 0;
    for (const b of scena.bryly) {
      const g = new THREE.BoxGeometry(M(b.s[0]), M(b.s[2]), M(b.s[1]));
      const m = new THREE.Mesh(g, MAT[b.k] || MAT.inne);
      m.position.set(M(b.p[0]), M(b.p[2]), -M(b.p[1]));
      m.rotation.y = b.a;
      m.castShadow = !["sciana", "okno", "drzwi_pok", "inne"].includes(b.k) && !!MAT[b.k];
      m.receiveShadow = true;
      scene.add(m);
      if (b.k === "front" || b.k === "witryna" || b.k === "cokol") {
        m.add(new THREE.LineSegments(new THREE.EdgesGeometry(g), kraw));
        // lico = dwie ściany na osi najmniejszego wymiaru (grubości): s[0] -> ±x, s[1] -> ±z, s[2] -> ±y. Grupy
        // BoxGeometry (+x, -x, +y, -y, +z, -z, po 6 indeksów) scalamy w boki (materiał 0) i lico (materiał 1)
        const s = b.s, ax = s.indexOf(Math.min(...s)), a = [0, 24, 12][ax], W = ax === 0 ? s[1] : s[0], H = ax === 2 ? s[1] : s[2];
        g.clearGroups();
        if (a) g.addGroup(0, a, 0);
        g.addGroup(a, 12, 1);
        if (a < 24) g.addGroup(a + 12, 24 - a, 0);
        // uchwyt (Góra albo Dół z pola u sceny, domyślnie Góra jak w konfiguratorze) tylko na drzwiach i szufladach; bez typu
        // w scenie panel to lico na osi x, a element węższy niż 150 mm to blenda
        const bez = b.k === "cokol" || (zTypem ? ["blenda", "panel", "listwa"].includes(b.t) : ax === 0 || Math.min(W, H) < 150);
        fronty.push({ m, k: b.k, W, H, bez, u: b.u || "Góra", uv0: g.attributes.uv.array.slice() });
      }
      if (b.k === "witryna" && b.s[0] > 150 && b.s[2] > 150) m.add(new THREE.Mesh(new THREE.BoxGeometry(M(b.s[0] - 100), M(b.s[2] - 100), M(b.s[1] + 2)), szklo));
      if (b.k === "korpus") korpusy.push(m);
      // kierunek kamery z drzwi i szuflad; front z „t” (blenda | panel | listwa) nie wyznacza lica zabudowy
      if (b.k === "front" && !b.t) { const s = b.s[0] * b.s[2]; nx += Math.sin(b.a) * s; nz += Math.cos(b.a) * s; }   // lico frontu patrzy w -y lokalne
      if (b.k === "sciana") sciany[b.i] = { m: [m], n: new THREE.Vector3(b.n[0], 0, -b.n[1]), p: m.position.clone() };
      else if (b.i >= 0 && sciany[b.i]) sciany[b.i].m.push(m);
    }
    const plaski = (pts, z, t, material) => {
      const sh = new THREE.Shape(pts.map(p => new THREE.Vector2(M(p[0]), M(p[1]))));
      const m = new THREE.Mesh(t ? new THREE.ExtrudeGeometry(sh, { depth: M(t), bevelEnabled: false }) : new THREE.ShapeGeometry(sh), material);
      m.rotation.x = -Math.PI / 2;      // (x, y) planera -> (x, -z) three, wyciągnięcie w górę
      m.position.y = M(z);
      m.castShadow = !!t; m.receiveShadow = true;
      scene.add(m);
    };
    for (const t of scena.blaty || []) plaski(t.poly, t.z, t.t, mat("#cbc5bb", { roughness: 0.7 }));
    const podloga = mat("#dccfbe", { roughness: 0.9 });
    for (const p of scena.podloga || []) plaski(p, 0, 0, podloga);

    // kadr: korpusy do 3,5 m od mediany (bez szafek z innych pomieszczeń planu)
    if (korpusy.length) {
      const med = a => a.sort((x, y) => x - y)[a.length >> 1];
      const mx = med(korpusy.map(m => m.position.x)), mz = med(korpusy.map(m => m.position.z));
      const blisko = korpusy.filter(m => Math.hypot(m.position.x - mx, m.position.z - mz) < 3.5);
      (blisko.length ? blisko : korpusy).forEach(m => kuchnia.expandByObject(m));
    }
    if (kuchnia.isEmpty()) kuchnia.setFromObject(scene);
    const c = kuchnia.getCenter(new THREE.Vector3()), size = kuchnia.getSize(new THREE.Vector3());
    const r = Math.max(size.x, size.z, 2.4);
    const dir = new THREE.Vector3(nx, 0, nz);
    if (dir.lengthSq() < 1e-9) dir.set(0, 0, 1);
    dir.normalize().applyAxisAngle(new THREE.Vector3(0, 1, 0), 0.45);   // widok z ukosa
    const target = new THREE.Vector3(c.x, Math.min(c.y, 1), c.z);
    const camera = new THREE.PerspectiveCamera(45, 1, 0.05, 100);
    camera.position.copy(target).addScaledVector(dir, r * 1.15).add(new THREE.Vector3(0, r * 0.75, 0));   // wyżej: wyspa i blat nie zasłaniają dolnych frontów

    scene.add(new THREE.HemisphereLight(0xffffff, 0xcfc6b8, 1.6));
    const fill = new THREE.DirectionalLight(0xffffff, 0.9);       // od strony kamery: lica frontów nie szarzeją
    fill.position.copy(c).addScaledVector(dir, 5).add(new THREE.Vector3(0, 1.5, 0));
    fill.target.position.copy(c);
    scene.add(fill, fill.target);
    const sun = new THREE.DirectionalLight(0xffffff, 1.3);
    sun.position.copy(c).add(new THREE.Vector3(dir.x * 3 + 2, 6, dir.z * 3 + 1));
    sun.target.position.copy(c);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    Object.assign(sun.shadow.camera, { left: -r, right: r, top: r, bottom: -r, near: 0.1, far: 30 });
    sun.shadow.normalBias = 0.02;
    scene.add(sun, sun.target);

    const ctl = new OrbitControls(camera, cv);
    ctl.target.copy(target);
    ctl.enableDamping = true;
    ctl.maxPolarAngle = Math.PI / 2 - 0.03;
    ctl.minDistance = 0.8;
    ctl.maxDistance = r * 3;
    ctl.update();
    // dotyk: jeden palec przewija stronę, dwa obracają i przybliżają; mysz: przeciąganie obraca, kółko przewija stronę,
    // Ctrl/Cmd + kółko (i gest szczypania na gładziku) przybliża
    if (window.matchMedia && matchMedia("(pointer: coarse)").matches) {
      ctl.touches = { ONE: null, TWO: THREE.TOUCH.DOLLY_ROTATE };
      cv.style.touchAction = "pan-y";
    }
    const kolko = e => { if (!e.ctrlKey && !e.metaKey) e.stopPropagation(); };   // przechwycenie na kontenerze wyprzedza OrbitControls
    el.addEventListener("wheel", kolko, { capture: true, passive: true });

    let zyje = true, raf = 0;
    const v = new THREE.Vector3(), u = new THREE.Vector3();
    const rysuj = () => {
      raf = 0;
      ctl.update();                   // przy wygaszaniu ruchu zgłasza „change”, a ten zamawia kolejną klatkę
      for (const s of sciany) {
        if (!s) continue;
        const widac = v.copy(camera.position).sub(s.p).dot(s.n) * u.copy(ctl.target).sub(s.p).dot(s.n) > 0;
        s.m.forEach(m => { m.visible = widac; });
      }
      renderer.render(scene, camera);
    };
    const zadaj = () => { if (zyje && !raf) raf = requestAnimationFrame(rysuj); };
    ctl.addEventListener("change", zadaj);
    cv.addEventListener("webglcontextrestored", zadaj);   // three odtwarza kontekst, ale sam nie rysuje
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth, h = el.clientHeight;
      if (!w || !h) return;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      zadaj();
    });
    ro.observe(el);
    zadaj();

    // ---------- wygląd frontów ----------
    // tekstury i materiały lica wg klucza (styl, kolor, wymiar lica...), po każdym wyglądzie zwalniamy te, których nie
    // użył ani on, ani poprzedni
    const ANISO = Math.min(8, renderer.capabilities.getMaxAnisotropy()), tex = o.tex || {};
    const pamiec = new Map();
    let nr = 0, poprzedni = 0;
    // kanwa rysunku zerowana od razu: Safari na iOS oddaje pamięć kanw dopiero przy GC (limit wszystkich kanw na stronie)
    const zwolnij = e => e.p.then(r => { r.map.dispose(); if (r.map.isCanvasTexture) r.map.image.width = r.map.image.height = 0; r.lico.dispose(); if (r.bok) r.bok.dispose(); }, () => {});
    const uzyj = (klucz, my, zrob) => {
      let e = pamiec.get(klucz);
      if (!e) {               // nieudane (np. sieć przy skanie) nie zostaje w pamięci: kolejny wybór próbuje od nowa
        pamiec.set(klucz, e = {});
        e.p = zrob().catch(err => { if (pamiec.get(klucz) === e) pamiec.delete(klucz); throw err; });
      }
      e.nr = my;
      return e.p;
    };
    // lakier: rysunek frontu z KnfFront -> SVG (data URI) -> canvas -> tekstura. Sonda: ten sam obraz na kanwie 1 x 1
    // i getImageData - rzuca, gdy przeglądarka skaziła obraz (wtedy kolor płaski). Kanwy w pamięci RAM (willReadFrequently):
    // rastrowanie na CPU i zwykłe wgranie do WebGL zamiast odczytu z GPU - w teście 2-3 razy szybciej
    async function rysunek(f, styl, hex, texSvg) {
      const px = Math.min(PXMM, MAXPX / Math.max(f.W, f.H)), cw = Math.max(8, Math.round(f.W * px)), ch = Math.max(8, Math.round(f.H * px));
      const S = { style: styl, hex, loc: f.u, hinge: "", horiz: false, gloss: false, customOn: false, locCustom: f.bez };
      const { defs, g } = window.KnfFront.svg(S, f.W, f.H, 1 / px, "pz-", { tex: texSvg, cat: () => "lakier" });
      const img = new Image();
      await new Promise((ok, zle) => {
        img.onload = ok; img.onerror = zle;
        img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${cw}" height="${ch}" viewBox="0 0 ${f.W} ${f.H}" preserveAspectRatio="none"><defs>${defs}</defs>${g}</svg>`);
      });
      const sonda = document.createElement("canvas").getContext("2d", { willReadFrequently: true });
      sonda.canvas.width = sonda.canvas.height = 1;
      sonda.drawImage(img, 0, 0, 1, 1);
      sonda.getImageData(0, 0, 1, 1);
      const c = document.createElement("canvas");
      c.width = cw; c.height = ch;
      c.getContext("2d", { willReadFrequently: true }).drawImage(img, 0, 0, cw, ch);
      const map = new THREE.CanvasTexture(c);
      map.colorSpace = THREE.SRGBColorSpace; map.anisotropy = ANISO;
      return { map, lico: new THREE.MeshStandardMaterial({ map, roughness: 0.55 }) };
    }
    // drewno: skan arkusza (RepeatWrapping), wspólny materiał lica; boki i krawędzie w średnim kolorze skanu
    async function skan(styl) {
      const map = await new THREE.TextureLoader().loadAsync(tex[styl].u);
      map.colorSpace = THREE.SRGBColorSpace; map.wrapS = map.wrapT = THREE.RepeatWrapping; map.anisotropy = ANISO;
      const c = document.createElement("canvas");
      c.width = c.height = 1;
      const x = c.getContext("2d", { willReadFrequently: true });
      x.imageSmoothingQuality = "high";
      x.drawImage(map.image, 0, 0, 1, 1);
      const [r, g, b] = x.getImageData(0, 0, 1, 1).data;
      const sr = new THREE.Color(`rgb(${r},${g},${b})`);
      return { map, sr, lico: new THREE.MeshStandardMaterial({ map, roughness: 0.62 }), bok: mat(sr.clone(), { roughness: 0.62 }) };
    }
    // UV lica w mm jak wzór drewna w KnfFront: liść lw x lh, kolejne liście obok, wyśrodkowanie; słoje poziome = obrót o 90 st.
    function uvDrewna(f, D, horiz) {
      const g = f.m.geometry, uv = g.attributes.uv, W = f.W, H = f.H, lw = D.w, lh = D.h;
      const Wf = horiz ? H : W, Lf = horiz ? W : H;
      const ox = (Wf - lw) / 2 - Math.ceil(Math.max(0, (Wf - lw) / 2) / lw) * lw, oy = (Lf - Math.min(Lf, lh)) / 2;
      uv.array.set(f.uv0);
      const gr = g.groups.find(x => x.materialIndex === 1);
      for (let i = gr.start; i < gr.start + gr.count; i++) {
        const v = g.index.getX(i), x = f.uv0[2 * v] * W, y = (1 - f.uv0[2 * v + 1]) * H;   // mm na licu, y w dół jak w SVG
        uv.setXY(v, ((horiz ? y : x) - ox) / lw, 1 - ((horiz ? W - x : y) - oy) / lh);
      }
      uv.needsUpdate = true;
    }
    async function zastosuj() {
      const my = ++nr, w = { ...wyg }, drewno = w.kat !== "lakier";
      try {
        let lica, bok, kr;
        if (drewno) {
          const d = await uzyj("d|" + w.styl, my, () => skan(w.styl));
          if (my !== nr) return;
          // słoje jak w konfiguratorze (forcedGrain): cokół poziomo, element węższy niż 10 cm wzdłuż dłuższego boku
          fronty.forEach(f => uvDrewna(f, tex[w.styl], f.k === "cokol" || (Math.min(f.W, f.H) < 100 && f.H < f.W)));
          lica = fronty.map(() => d.lico); bok = d.bok; kr = d.sr.clone().multiplyScalar(0.7);
        } else {
          // Round Oak: wkładka z miniatury skanu Dąb wklejona w SVG jako data URI
          const texSvg = w.styl === "Round Oak" && tex["Dąb"] ? { "Dąb": { u: await naDataUri(tex["Dąb"].m), w: tex["Dąb"].tw, h: tex["Dąb"].th } } : {};
          // Gładki (także cokoły i witryny w każdym lakierze) rysuje sam kolor z cienką krawędzią: lico w kolorze płaskim
          const r = await Promise.all(fronty.map(f => {
            const st = f.k === "front" ? w.styl : "Gładki";
            return st === "Gładki" ? null : uzyj([st, w.hex, f.W, f.H, f.bez, f.u, PXMM].join("|"), my, () => rysunek(f, st, w.hex, texSvg));
          }));
          if (my !== nr) return;
          fronty.forEach(f => { f.m.geometry.attributes.uv.array.set(f.uv0); f.m.geometry.attributes.uv.needsUpdate = true; });
          lica = r.map(x => x ? x.lico : front); bok = front; front.color.set(w.hex); kr = ciemn(front.color);
        }
        fronty.forEach((f, i) => { f.m.material = lica[i] === bok ? bok : [bok, lica[i]]; });   // jeden materiał = jedno wywołanie rysowania
        kraw.color.copy(kr);
        for (const [k, e] of pamiec) if (e.nr < poprzedni) { pamiec.delete(k); zwolnij(e); }
        poprzedni = my;
        zadaj();
      } catch (e) {          // bez tekstur: kolor płaski
        if (my !== nr) return;
        front.color.set(drewno ? DREWNO : w.hex); kraw.color.copy(ciemn(front.color));
        fronty.forEach(f => { f.m.material = front; });
        zadaj();
      }
    }
    let biezacy = zastosuj();      // obietnica ostatnio zamówionego wyglądu
    if (tex["Dąb"]) naDataUri(tex["Dąb"].m).catch(() => {});   // wkładka Round Oak gotowa, zanim klient wybierze styl

    const widok = {
      // wygląd frontów: kolor (hex) i styl z kategorią; obietnica spełnia się, gdy wygląd jest gotowy i klatka zamówiona
      // (wygląd zamówiony później ją wyprzedza); zmiana koloru przy drewnie nie zmienia frontów
      ustaw(x) {
        const n = { ...wyg, ...(x.kolor ? { hex: x.kolor } : {}), ...(x.styl ? { styl: x.styl, kat: x.kat || "lakier" } : {}) };
        const zmiana = n.styl !== wyg.styl || n.kat !== wyg.kat || (n.kat === "lakier" && n.hex !== wyg.hex);
        wyg = n;
        return zmiana ? (biezacy = zastosuj()) : biezacy;
      },
      kolor: h => widok.ustaw({ kolor: h }),
      zniszcz() {
        zyje = false; nr++; if (raf) cancelAnimationFrame(raf);
        ro.disconnect(); ctl.dispose(); el.removeEventListener("wheel", kolko, { capture: true });
        scene.traverse(x => { if (x.geometry) x.geometry.dispose(); if (x.material) [].concat(x.material).forEach(m => m.dispose()); });
        pamiec.forEach(zwolnij); pamiec.clear();
        sun.dispose();
        renderer.dispose(); renderer.forceContextLoss(); cv.remove();   // zwalnia kontekst WebGL od razu, nie dopiero przy GC
      }
    };
    return widok;
  }

  window.PlanerZestaw3D = { pokaz, zaladuj };
})();
